import { z } from 'zod'

import { BillingScheduleCadenceSchema } from './billing-schedule'
import { decimalSchema } from './prisma-zod'

// Contract status enum
export enum ContractStatusEnum {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  NEGOTIATING = 'NEGOTIATING',
  SIGNED = 'SIGNED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export enum ContractSowStatusEnum {
  NOT_STARTED = 'NOT_STARTED',
  IN_DEV = 'IN_DEV',
  IN_REVIEW = 'IN_REVIEW',
  WITH_CLIENT = 'WITH_CLIENT',
  EXECUTED = 'EXECUTED',
  NOT_NEEDED = 'NOT_NEEDED',
}

// What happens once the contract's term ends (explicit endDate, else
// startDate + retainerTermMonths; open-ended contracts have no term and
// ignore this). Wired to the retainer engine only for now.
export enum ContractPostTermBehaviorEnum {
  END = 'END',
  MONTH_TO_MONTH = 'MONTH_TO_MONTH',
}

export enum ContractRenewalStatusEnum {
  NOT_RENEWABLE = 'NOT_RENEWABLE',
  CURRENT = 'CURRENT',
  UPCOMING = 'UPCOMING',
  RENEWING = 'RENEWING',
  AT_RISK = 'AT_RISK',
  ENDED = 'ENDED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Behavior discriminator for a contract type. Drives which form fields are
 * surfaced, which are collapsed, whether currency is required, and which
 * type-specific section renders. Keyed off `kind` (not the type id) so custom
 * or renamed types can opt into a behavior.
 */
export type ContractTypeKind =
  | 'general'
  | 'retainer'
  | 'subscription'
  | 'service'
  | 'legal'
  | 'license'

export interface ContractTypeConfig {
  id: string
  name: string
  color: string
  description?: string
  /** Behavior profile. Defaults to 'general' (show everything) when unset. */
  kind?: ContractTypeKind
}

export interface ContractLegalStatusConfig {
  id: string
  name: string
  color: string
  isDefault?: boolean
}

/**
 * Server-consumed slice of the `contract.settings` system preference. The full
 * shape (types, legal statuses, reminders) lives in the web normalizer
 * (apps/web contract-settings.ts), which derives its requireApproval default
 * from this schema so the two never drift. Passthrough keeps the unknown keys
 * intact. Both flags default false: enforcement is opt-in, and existing
 * persisted values were reset to false by migration (contract_approval_requests
 * for requireApproval, reset_require_signature for requireSignature) because
 * they were saved by whole-object saves, not user intent.
 */
export const ContractModuleSettingsSchema = z
  .object({
    requireApproval: z.boolean().default(false),
    requireSignature: z.boolean().default(false),
    // Defaults true (matching the web default): contract update capture in the
    // audit middleware stays on unless the org explicitly turns versioning off.
    enableVersioning: z.boolean().default(true),
  })
  .passthrough()

export type ContractModuleSettings = z.infer<
  typeof ContractModuleSettingsSchema
>

export const DEFAULT_CONTRACT_TYPES: ContractTypeConfig[] = [
  {
    id: 'msa',
    name: 'Master Service Agreement',
    color: '#3B82F6',
    description: 'Governing terms for ongoing services',
    kind: 'legal',
  },
  {
    id: 'sow',
    name: 'Statement of Work',
    color: '#8B5CF6',
    description: 'Project scope and delivery agreement',
    kind: 'service',
  },
  {
    id: 'nda',
    name: 'Non-Disclosure Agreement',
    color: '#F59E0B',
    description: 'Confidentiality and information protection',
    kind: 'legal',
  },
  {
    id: 'subscription',
    name: 'Subscription Agreement',
    color: '#10B981',
    description: 'Recurring service subscription terms',
    kind: 'subscription',
  },
  {
    id: 'retainer',
    name: 'Retainer Agreement',
    color: '#EC4899',
    description: 'Reserved capacity and retained services',
    kind: 'retainer',
  },
  {
    id: 'licensing',
    name: 'Licensing Agreement',
    color: '#06B6D4',
    description: 'License terms for IP or software use',
    kind: 'license',
  },
]

// Contract status colors
export const CONTRACT_STATUS_COLORS: Record<ContractStatusEnum, string> = {
  [ContractStatusEnum.DRAFT]: '#6b7280',
  [ContractStatusEnum.SENT]: '#3b82f6',
  [ContractStatusEnum.NEGOTIATING]: '#f59e0b',
  [ContractStatusEnum.SIGNED]: '#8b5cf6',
  [ContractStatusEnum.ACTIVE]: '#10b981',
  [ContractStatusEnum.COMPLETED]: '#1ead5b',
  [ContractStatusEnum.CANCELLED]: '#ef4444',
  [ContractStatusEnum.EXPIRED]: '#64748b',
}

// Contract status display names
export const CONTRACT_STATUS_DISPLAY_NAMES: Record<ContractStatusEnum, string> =
  {
    [ContractStatusEnum.DRAFT]: 'Draft',
    [ContractStatusEnum.SENT]: 'Sent',
    [ContractStatusEnum.NEGOTIATING]: 'Negotiating',
    [ContractStatusEnum.SIGNED]: 'Signed',
    [ContractStatusEnum.ACTIVE]: 'Active',
    [ContractStatusEnum.COMPLETED]: 'Completed',
    [ContractStatusEnum.CANCELLED]: 'Canceled',
    [ContractStatusEnum.EXPIRED]: 'Expired',
  }

export const DEFAULT_CONTRACT_LEGAL_STATUSES: ContractLegalStatusConfig[] =
  Object.values(ContractStatusEnum).map(status => ({
    id: status,
    name: CONTRACT_STATUS_DISPLAY_NAMES[status],
    color: CONTRACT_STATUS_COLORS[status],
    isDefault: status === ContractStatusEnum.DRAFT,
  }))

export const CONTRACT_SOW_STATUS_COLORS: Record<ContractSowStatusEnum, string> =
  {
    [ContractSowStatusEnum.NOT_STARTED]: '#6b7280',
    [ContractSowStatusEnum.IN_DEV]: '#0ea5e9',
    [ContractSowStatusEnum.IN_REVIEW]: '#f59e0b',
    [ContractSowStatusEnum.WITH_CLIENT]: '#8b5cf6',
    [ContractSowStatusEnum.EXECUTED]: '#10b981',
    [ContractSowStatusEnum.NOT_NEEDED]: '#64748b',
  }

export const CONTRACT_SOW_STATUS_DISPLAY_NAMES: Record<
  ContractSowStatusEnum,
  string
> = {
  [ContractSowStatusEnum.NOT_STARTED]: 'Not Started',
  [ContractSowStatusEnum.IN_DEV]: 'In Development',
  [ContractSowStatusEnum.IN_REVIEW]: 'In Review',
  [ContractSowStatusEnum.WITH_CLIENT]: 'With Client',
  [ContractSowStatusEnum.EXECUTED]: 'Executed',
  [ContractSowStatusEnum.NOT_NEEDED]: 'Not Needed',
}

export const CONTRACT_RENEWAL_STATUS_COLORS: Record<
  ContractRenewalStatusEnum,
  string
> = {
  [ContractRenewalStatusEnum.NOT_RENEWABLE]: '#64748b',
  [ContractRenewalStatusEnum.CURRENT]: '#10b981',
  [ContractRenewalStatusEnum.UPCOMING]: '#0ea5e9',
  [ContractRenewalStatusEnum.RENEWING]: '#8b5cf6',
  [ContractRenewalStatusEnum.AT_RISK]: '#ef4444',
  [ContractRenewalStatusEnum.ENDED]: '#334155',
  [ContractRenewalStatusEnum.UNKNOWN]: '#6b7280',
}

export const CONTRACT_RENEWAL_STATUS_DISPLAY_NAMES: Record<
  ContractRenewalStatusEnum,
  string
> = {
  [ContractRenewalStatusEnum.NOT_RENEWABLE]: 'Not Renewable',
  [ContractRenewalStatusEnum.CURRENT]: 'Current',
  [ContractRenewalStatusEnum.UPCOMING]: 'Upcoming',
  [ContractRenewalStatusEnum.RENEWING]: 'Renewing',
  [ContractRenewalStatusEnum.AT_RISK]: 'At Risk',
  [ContractRenewalStatusEnum.ENDED]: 'Ended',
  [ContractRenewalStatusEnum.UNKNOWN]: 'Unknown',
}

// Contract sort field enum
export enum ContractSortFieldEnum {
  TITLE = 'title',
  DESCRIPTION = 'description',
  STATUS = 'status',
  CONTRACTNUMBER = 'contractNumber',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// Contract schema
export const ContractSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  contractNumber: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  // Coerced so date-only strings in raw API payloads parse instead of
  // reaching Prisma and 500ing (billing schedule pass Phase 2.4.11).
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  // NOT on RetainerTermsSchema: that schema is snapshotted into every
  // ContractRetainerPeriod.termsSnapshot and is the plan-change payload, and
  // post-term behavior must be neither schedulable per-period nor copied into
  // historical snapshots.
  postTermBehavior: z
    .nativeEnum(ContractPostTermBehaviorEnum)
    .default(ContractPostTermBehaviorEnum.END),
  totalValue: decimalSchema.optional(),
  // T26: signed baseline value — immutable once set; totalValue is the current
  // (amended) value. changeOrderDelta = totalValue − baselineValue.
  baselineValue: decimalSchema.nullable().optional(),
  changeOrderDelta: decimalSchema.nullable().optional(),
  consumedAmount: decimalSchema.optional().default(0),
  consumedTimeAmount: decimalSchema.optional().default(0),
  consumedExpenseAmount: decimalSchema.optional().default(0),
  remainingAmount: decimalSchema.nullable().optional(),
  currency: z.string(),
  contractType: z.string(),
  paymentTerms: z.string().optional(),
  // Per-contract override of the billing profile's late-fee terms (A14).
  // Display text only; nothing computes fees from it.
  lateFeeTerms: z.string().nullable().optional(),
  status: z.string().default(ContractStatusEnum.DRAFT),
  sowStatus: z
    .nativeEnum(ContractSowStatusEnum)
    .nullable()
    .optional()
    .default(ContractSowStatusEnum.NOT_STARTED),
  renewalStatus: z
    .nativeEnum(ContractRenewalStatusEnum)
    .nullable()
    .optional()
    .default(ContractRenewalStatusEnum.UNKNOWN),
  renewalOwnerId: z.string().uuid().nullable().optional(),
  renewalOwner: z.any().nullable().optional(),
  renewalDueDate: z.coerce.date().nullable().optional(),
  isRenewable: z.boolean().default(false),
  renewalTerms: z.string().optional(),
  reminderDate: z.coerce.date().optional(),
  // These three were mistyped as z.date(); they are strings in the DB and the
  // UI. Corrected when create bodies started being parsed (Phase 2.4.11).
  // The *Name fields are WHO signed; signedByClient/signedByOrg below are WHEN
  // — separate columns, and the earlier mistyping came from conflating them.
  signedByEntityName: z.string().nullable().optional(),
  signedByOrgName: z.string().nullable().optional(),
  // Captured per party: the client and the org can countersign on different
  // days, so these are two dates, not one "signed on".
  signedByClient: z.coerce.date().nullable().optional(),
  signedByOrg: z.coerce.date().nullable().optional(),
  documentUrl: z.string().nullable().optional(),
  settings: z.any().default({}),
  billingModel: z.enum(['NONE', 'RETAINER', 'SUBSCRIPTION']).default('NONE'),
  subscriptionSeats: z.number().int().optional().nullable(),
  // Structured billing terms — source of truth for materialize-now schedule
  // generation. billingTermsAmount is the explicit per-period amount; if
  // billingTermsCount is null the series length derives from startDate→endDate.
  billingTermsCadence: BillingScheduleCadenceSchema.nullable().optional(),
  billingTermsAmount: decimalSchema.nullable().optional(),
  billingTermsStartDate: z.coerce.date().nullable().optional(),
  billingTermsCount: z.number().int().positive().nullable().optional(),
  billingTermsCategoryId: z.string().uuid().nullable().optional(),
  billingTermsLastGeneratedAt: z.date().nullable().optional(),
  // Map of String(periodIndex) -> amount; null map or missing key means "use
  // billingTermsAmount" for that period.
  billingTermsPeriodOverrides: z
    .record(z.string(), decimalSchema)
    .nullable()
    .optional(),
  isRetainer: z.boolean().default(false),
  // Opt-in for the scheduled per-period draft run (invoicing-ops Phase 4).
  // Optional, no default: read shape only, and a default would inject the
  // key into payloads that spread this schema.
  retainerAutoDraftInvoices: z.boolean().optional(),
  retainerHoursMonthly: decimalSchema.optional().nullable(),
  retainerAmountMonthly: decimalSchema.optional().nullable(),
  retainerRollover: z.boolean().default(false),
  retainerMaxRollover: decimalSchema.optional().nullable(),
  retainerRolloverExpiryMonths: z.number().int().default(1),
  retainerOverageBillingMode: z
    .enum(['NONE', 'HOURLY_RATE', 'PREMIUM_MULTIPLIER'])
    .default('HOURLY_RATE'),
  retainerOverageHourlyRate: decimalSchema.optional().nullable(),
  retainerOveragePremiumMultiplier: decimalSchema.optional().nullable(),
  retainerTermMonths: z.number().int().optional().nullable(),
  retainerDiscountPercent: decimalSchema.optional().nullable(),
  retainerResponseSla: z.string().optional().nullable(),
  serviceRetainerPlanId: z.string().uuid().optional().nullable(),
  serviceRetainerPlanVersionId: z.string().uuid().optional().nullable(),
  retainerPlanId: z.string().optional().nullable(),
  retainerPlanName: z.string().optional().nullable(),
  retainerPortalSettings: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
})

export type Contract = z.infer<typeof ContractSchema>

// Create Contract schema
export const CreateContractSchema = ContractSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).extend({
  address: z.any().optional(),
})

export type CreateContract = z.infer<typeof CreateContractSchema>

// Update Contract schema
// T26: when an update amends totalValue, a change-order journal entry is
// recorded automatically; these optional fields annotate that entry.
export const UpdateContractSchema = CreateContractSchema.partial().extend({
  changeOrderReason: z.string().nullable().optional(),
  changeOrderBilled: z.boolean().optional(),
})

export type UpdateContract = z.infer<typeof UpdateContractSchema>

// =====================
// T26: Contract change orders (baseline vs current value)
// =====================

export const ContractChangeOrderSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  contractId: z.string().uuid(),
  changeType: z.string().default('AMENDMENT'), // AMENDMENT | CORRECTION
  deltaValue: decimalSchema, // signed: newValue - previousValue
  previousValue: decimalSchema.nullable().optional(),
  newValue: decimalSchema.nullable().optional(),
  reason: z.string().nullable().optional(),
  isBilled: z.boolean().default(false),
  createdBy: z.string().uuid().nullable().optional(),
  creator: z.any().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ContractChangeOrder = z.infer<typeof ContractChangeOrderSchema>

export interface CreateContractChangeOrderInput {
  /** Signed value delta applied to the contract's current value. */
  deltaValue: number
  reason?: string | null
  /** Was this scope change billed to the client? false = unbilled scope creep. */
  isBilled?: boolean
  changeType?: 'AMENDMENT' | 'CORRECTION'
}

export interface UpdateContractChangeOrderInput {
  isBilled?: boolean
  reason?: string | null
}

/** U5 payload: baseline vs current decomposition for one contract. */
export interface ContractChangeOrderSummary {
  contractId: string
  /** Signed baseline value (null if the contract never had a value). */
  baselineValue: number | null
  /** Current (amended) contract value. */
  currentValue: number | null
  /** currentValue − baselineValue; equals the summed change-order deltas. */
  changeOrderDelta: number
  /** Portion of the delta that was billed to the client. */
  billedChangeOrderDelta: number
  /** Portion absorbed without billing — the scope-creep margin leak. */
  unbilledChangeOrderDelta: number
  changeOrders: ContractChangeOrder[]
}

// Contract response schema
export const ContractResponseSchema = z.object({
  data: ContractSchema,
  message: z.string().optional(),
})

export type ContractResponse = z.infer<typeof ContractResponseSchema>

// Contracts response schema
export const ContractsResponseSchema = z.object({
  data: z.array(ContractSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type ContractsResponse = z.infer<typeof ContractsResponseSchema>

export const RetainerPortalSettingsSchema = z.object({
  showInPortal: z.boolean().default(true),
  showHistory: z.boolean().default(true),
  showProjectBreakdown: z.boolean().default(true),
  showMemberBreakdown: z.boolean().default(false),
  showOverageProjection: z.boolean().default(true),
})

export type RetainerPortalSettings = z.infer<
  typeof RetainerPortalSettingsSchema
>

export const RetainerTermsSchema = z.object({
  isRetainer: z.boolean(),
  serviceRetainerPlanId: z.string().uuid().nullable().optional(),
  serviceRetainerPlanVersionId: z.string().uuid().nullable().optional(),
  planId: z.string().nullable().optional(),
  monthlyHours: z.number().min(0),
  monthlyAmount: z.number().min(0).nullable().optional(),
  rolloverEnabled: z.boolean(),
  maxRolloverHours: z.number().min(0).nullable().optional(),
  rolloverExpiryMonths: z.number().int().min(1).default(1),
  overageBillingMode: z
    .enum(['NONE', 'HOURLY_RATE', 'PREMIUM_MULTIPLIER'])
    .default('HOURLY_RATE'),
  overageHourlyRate: z.number().min(0).nullable().optional(),
  overagePremiumMultiplier: z.number().min(0).nullable().optional(),
  termMonths: z.number().int().min(1).nullable().optional(),
  discountPercent: z.number().min(0).max(100).nullable().optional(),
  responseSla: z.string().nullable().optional(),
  planName: z.string().nullable().optional(),
  portalSettings: RetainerPortalSettingsSchema.optional(),
  // Opt-in for the scheduled per-period draft-invoice run (invoicing-ops
  // Phase 4). Optional with no default so older payloads leave the stored
  // flag untouched; drafts only, never auto-send.
  autoDraftInvoices: z.boolean().optional(),
})

export type RetainerTerms = z.infer<typeof RetainerTermsSchema>

export const TermDiscountTierSchema = z.object({
  months: z.number().int().min(1),
  discountPercent: z.number().min(0).max(100),
})

export type TermDiscountTier = z.infer<typeof TermDiscountTierSchema>

export const ServiceRetainerPlanVersionTermsSchema = z.object({
  label: z.string().nullable().optional(),
  includedQuantity: z.number().min(0),
  unitLabel: z.string().default('hours'),
  billingCadence: z.string().default('MONTHLY'),
  fixedFee: z.number().min(0).nullable().optional(),
  rolloverEnabled: z.boolean().default(false),
  rolloverCapQuantity: z.number().min(0).nullable().optional(),
  rolloverExpiryPeriods: z.number().int().min(1).default(1),
  overageBillingMode: z
    .enum(['NONE', 'HOURLY_RATE', 'PREMIUM_MULTIPLIER'])
    .default('HOURLY_RATE'),
  overageHourlyRate: z.number().min(0).nullable().optional(),
  overagePremiumMultiplier: z.number().min(0).nullable().optional(),
  commitmentPeriods: z.number().int().min(1).nullable().optional(),
  discountPercent: z.number().min(0).max(100).nullable().optional(),
  termDiscounts: z.array(TermDiscountTierSchema).nullable().optional(),
  responseSla: z.string().nullable().optional(),
  allowMidCommitmentSwitch: z.boolean().default(true),
  switchNoticeDays: z.number().int().min(0).default(0),
  portalSettings: RetainerPortalSettingsSchema.optional(),
})

/**
 * Resolve a discount % from a plan version's term-discount table for a given
 * commitment length. Picks the longest tier whose `months` ≤ requested length.
 * Returns null if no table is configured or no tier matches.
 */
export function resolveTermDiscountPercent(
  termDiscounts: TermDiscountTier[] | null | undefined,
  termMonths: number | null | undefined,
): number | null {
  if (!termDiscounts || termDiscounts.length === 0 || termMonths == null)
    return null
  const eligible = termDiscounts
    .filter(tier => tier.months <= termMonths)
    .sort((a, b) => b.months - a.months)
  const winner = eligible[0]
  return winner ? winner.discountPercent : null
}

export type ServiceRetainerPlanVersionTerms = z.infer<
  typeof ServiceRetainerPlanVersionTermsSchema
>

export const ServiceRetainerPlanVersionSchema =
  ServiceRetainerPlanVersionTermsSchema.extend({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    planId: z.string().uuid(),
    version: z.number().int(),
    status: z.enum(['ACTIVE', 'ARCHIVED']),
    termsSnapshot: z.any().default({}),
    createdAt: z.string().or(z.date()),
    creatorName: z.string().nullable().optional(),
  })

export type ServiceRetainerPlanVersion = z.infer<
  typeof ServiceRetainerPlanVersionSchema
>

export const ServiceRetainerPlanGroupSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  displayOrder: z.number().int().default(0),
  status: z.enum(['ACTIVE', 'ARCHIVED']),
  metadata: z.any().default({}),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
  archivedAt: z.string().or(z.date()).nullable().optional(),
})

export type ServiceRetainerPlanGroup = z.infer<
  typeof ServiceRetainerPlanGroupSchema
>

export interface CreateServiceRetainerPlanGroupInput {
  name: string
  code?: string | null
  description?: string | null
}

export interface UpdateServiceRetainerPlanGroupInput {
  name?: string
  code?: string | null
  description?: string | null
  status?: 'ACTIVE' | 'ARCHIVED'
}

export const ServiceRetainerPlanSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  groupId: z.string().uuid().nullable().optional(),
  group: ServiceRetainerPlanGroupSchema.nullable().optional(),
  displayOrder: z.number().int().default(0),
  name: z.string(),
  code: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  currency: z.string(),
  status: z.enum(['ACTIVE', 'ARCHIVED']),
  planType: z.enum(['RETAINER', 'SUBSCRIPTION']).default('RETAINER'),
  metadata: z.any().default({}),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
  archivedAt: z.string().or(z.date()).nullable().optional(),
  currentVersion: ServiceRetainerPlanVersionSchema.nullable(),
  versions: z.array(ServiceRetainerPlanVersionSchema).default([]),
})

export type ServiceRetainerPlan = z.infer<typeof ServiceRetainerPlanSchema>

export interface CreateServiceRetainerPlanInput {
  name: string
  code?: string | null
  description?: string | null
  currency?: string
  groupId?: string | null
  planType?: 'RETAINER' | 'SUBSCRIPTION'
  metadata?: Record<string, unknown>
  version: ServiceRetainerPlanVersionTerms
}

export interface UpdateServiceRetainerPlanInput {
  name?: string
  code?: string | null
  description?: string | null
  currency?: string
  groupId?: string | null
  planType?: 'RETAINER' | 'SUBSCRIPTION'
  metadata?: Record<string, unknown>
  status?: 'ACTIVE' | 'ARCHIVED'
}

export type CreateServiceRetainerPlanVersionInput =
  ServiceRetainerPlanVersionTerms

export const RetainerPlanChangeSchema = z.object({
  id: z.string().uuid(),
  contractId: z.string().uuid(),
  currentPlanId: z.string().nullable(),
  currentPlanName: z.string().nullable(),
  nextServiceRetainerPlanId: z.string().uuid().nullable().optional(),
  nextServiceRetainerPlanVersionId: z.string().uuid().nullable().optional(),
  nextPlanId: z.string().nullable(),
  nextPlanName: z.string().nullable(),
  effectiveDate: z.string().or(z.date()),
  terms: RetainerTermsSchema,
  status: z.enum(['SCHEDULED', 'APPLIED', 'CANCELLED']),
  reason: z.string().nullable(),
  createdAt: z.string().or(z.date()),
  creatorName: z.string().nullable().optional(),
  appliedAt: z.string().or(z.date()).nullable(),
  cancelledAt: z.string().or(z.date()).nullable(),
})

export type RetainerPlanChange = z.infer<typeof RetainerPlanChangeSchema>

export const ScheduleRetainerPlanChangeSchema = z.object({
  effectiveDate: z.string().or(z.date()),
  serviceRetainerPlanVersionId: z.string().uuid().nullable().optional(),
  terms: RetainerTermsSchema,
  reason: z.string().optional(),
})

export type ScheduleRetainerPlanChangeInput = z.infer<
  typeof ScheduleRetainerPlanChangeSchema
>

export const RetainerUsageBreakdownSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string(),
  hours: z.number(),
})

export type RetainerUsageBreakdown = z.infer<
  typeof RetainerUsageBreakdownSchema
>

export const RetainerAdjustmentSchema = z.object({
  id: z.string().uuid(),
  adjustmentType: z.enum(['CREDIT', 'DEBIT']),
  hours: z.number(),
  reason: z.string(),
  clientVisible: z.boolean(),
  createdAt: z.string().or(z.date()),
  creatorName: z.string().nullable().optional(),
})

export type RetainerAdjustment = z.infer<typeof RetainerAdjustmentSchema>

export const RetainerPeriodSchema = z.object({
  id: z.string().uuid(),
  contractId: z.string().uuid(),
  entityId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  periodStart: z.string().or(z.date()),
  periodEnd: z.string().or(z.date()),
  serviceRetainerPlanId: z.string().uuid().nullable().optional(),
  serviceRetainerPlanVersionId: z.string().uuid().nullable().optional(),
  planId: z.string().nullable().optional(),
  planName: z.string().nullable().optional(),
  monthlyAmount: z.number().nullable().optional(),
  discountPercent: z.number().default(0),
  discountAmount: z.number().default(0),
  netMonthlyAmount: z.number().nullable().optional(),
  overageBillingMode: z
    .enum(['NONE', 'HOURLY_RATE', 'PREMIUM_MULTIPLIER'])
    .optional(),
  overageHourlyRate: z.number().nullable().optional(),
  overagePremiumMultiplier: z.number().nullable().optional(),
  termsSnapshot: RetainerTermsSchema.optional(),
  includedHours: z.number(),
  rolloverInHours: z.number(),
  rolloverConsumedHours: z.number().default(0),
  rolloverExpiringHours: z.number(),
  adjustmentHours: z.number(),
  usedHours: z.number(),
  remainingHours: z.number(),
  overageHours: z.number(),
  pendingHours: z.number().default(0),
  unallocatedHours: z.number().default(0),
  forecastedUsageHours: z.number().nullable(),
  projectedOverageHours: z.number().nullable(),
  projectedOverageDate: z.string().or(z.date()).nullable(),
  usagePercent: z.number(),
  status: z.enum(['OPEN', 'OVER_LIMIT', 'CLOSED', 'REOPENED']),
  recalculatedAt: z.string().or(z.date()).nullable(),
  closedAt: z.string().or(z.date()).nullable(),
  projectBreakdown: z.array(RetainerUsageBreakdownSchema).default([]),
  memberBreakdown: z.array(RetainerUsageBreakdownSchema).default([]),
  roleBreakdown: z.array(RetainerUsageBreakdownSchema).default([]),
  adjustments: z.array(RetainerAdjustmentSchema).default([]),
})

export type RetainerPeriod = z.infer<typeof RetainerPeriodSchema>

export const RetainerOverviewSchema = z.object({
  // Business calendar for midnight/resume refresh without an extra settings request.
  calendar: z.object({ timezone: z.string(), today: z.string() }).optional(),
  contract: z.object({
    id: z.string().uuid(),
    title: z.string(),
    entityId: z.string().uuid(),
    entityName: z.string().nullable(),
    projectId: z.string().uuid().nullable(),
    projectName: z.string().nullable(),
    currency: z.string(),
    terms: RetainerTermsSchema,
  }),
  activePeriod: RetainerPeriodSchema.nullable(),
  periods: z.array(RetainerPeriodSchema),
  planChanges: z.array(RetainerPlanChangeSchema).default([]),
  // Real current/next anniversary window starts (YYYY-MM-DD), independent of
  // any ?period= param — the UI uses these for plan-change effective dates.
  currentPeriodStart: z.string().or(z.date()).optional(),
  nextPeriodStart: z.string().or(z.date()).optional(),
  // Non-closed periods still anchored to an older startDate. Drives the
  // "realign periods" prompt after the contract start date is edited.
  misalignedOpenPeriodCount: z.number().default(0),
})

export type RetainerOverview = z.infer<typeof RetainerOverviewSchema>

export interface RealignRetainerPeriodsResult {
  /** Periods re-anchored onto the current start date's anniversary windows. */
  realigned: number
  /** Empty duplicate periods removed after collapsing onto a shared window. */
  removed: number
  /** Periods left in place because they carry usage, adjustments, or invoices. */
  conflicts: number
}

export interface CreateRetainerAdjustmentInput {
  adjustmentType: 'CREDIT' | 'DEBIT'
  hours: number
  reason: string
  clientVisible?: boolean
}

export interface RetainerDraftInvoiceResponse {
  retainerInvoiceId: string | null
  overageInvoiceId: string | null
}

export const RetainerPlanChangePreviewSchema = z.object({
  effectiveDate: z.string(),
  isBackdate: z.boolean(),
  proposedTerms: RetainerTermsSchema,
  affected: z.array(
    z.object({
      periodId: z.string().uuid(),
      periodStart: z.string(),
      periodEnd: z.string(),
      status: z.enum(['OPEN', 'OVER_LIMIT', 'CLOSED', 'REOPENED']),
      before: z.object({
        includedHours: z.number(),
        rolloverInHours: z.number(),
        monthlyAmount: z.number(),
        discountPercent: z.number(),
        netMonthlyAmount: z.number(),
        remainingHours: z.number(),
        overageHours: z.number(),
        overageCost: z.number(),
        planName: z.string().nullable(),
      }),
      after: z.object({
        includedHours: z.number(),
        rolloverInHours: z.number(),
        monthlyAmount: z.number(),
        discountPercent: z.number(),
        netMonthlyAmount: z.number(),
        remainingHours: z.number(),
        overageHours: z.number(),
        overageCost: z.number(),
        planName: z.string().nullable(),
      }),
      deltas: z.object({
        hoursDelta: z.number(),
        dollarsDelta: z.number(),
      }),
    }),
  ),
  blockedClosedPeriods: z.array(
    z.object({
      periodId: z.string().uuid(),
      periodStart: z.string(),
      periodEnd: z.string(),
      usedHours: z.number(),
      overageHours: z.number(),
    }),
  ),
  totals: z.object({
    hoursDelta: z.number(),
    dollarsDelta: z.number(),
    affectedCount: z.number(),
    blockedCount: z.number(),
  }),
})

export type RetainerPlanChangePreview = z.infer<
  typeof RetainerPlanChangePreviewSchema
>

export const RetainerSummarySchema = z.object({
  contractId: z.string().uuid(),
  contractTitle: z.string(),
  contractStatus: z.string(),
  entityId: z.string().uuid(),
  entityName: z.string().nullable(),
  currency: z.string(),
  planName: z.string().nullable(),
  periodStart: z.string().or(z.date()),
  periodEnd: z.string().or(z.date()),
  includedHours: z.number(),
  rolloverInHours: z.number(),
  adjustmentHours: z.number(),
  usedHours: z.number(),
  remainingHours: z.number(),
  overageHours: z.number(),
  rolloverExpiringHours: z.number(),
  forecastedUsageHours: z.number().nullable(),
  projectedOverageHours: z.number().nullable(),
  projectedOverageDate: z.string().or(z.date()).nullable(),
  monthlyAmount: z.number().nullable(),
  usagePercent: z.number(),
  status: z.enum(['OPEN', 'OVER_LIMIT', 'CLOSED', 'REOPENED']),
})

export type RetainerSummary = z.infer<typeof RetainerSummarySchema>

export const EligibleRetainerSchema = z.object({
  contractId: z.string().uuid(),
  contractTitle: z.string(),
  planName: z.string().nullable(),
  monthlyHours: z.number(),
  scope: z.enum(['PROJECT', 'ENTITY']),
  periodStart: z.string().or(z.date()),
  periodEnd: z.string().or(z.date()),
  usedHours: z.number(),
  remainingHours: z.number().nullable(),
  usagePercent: z.number(),
  overage: z.boolean(),
})

export type EligibleRetainer = z.infer<typeof EligibleRetainerSchema>
